'use client';
import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';

// English is the only locale inlined in the main bundle. es/ar/ja load on
// demand when the user (or geo-detect) switches away from English — keeps
// ~1.5 MB of unused dictionaries off the critical path.
import EN_STRINGS from '@/translations/en.json';

const SUPPORTED = ['en', 'es', 'ar', 'ja'];
const RTL_LANGS = ['ar'];

const COUNTRY_LANG = {
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es', EC: 'es',
  GT: 'es', CU: 'es', BO: 'es', DO: 'es', UY: 'es', PY: 'es', HN: 'es', SV: 'es',
  SA: 'ar', AE: 'ar', EG: 'ar', KW: 'ar', QA: 'ar', BH: 'ar', OM: 'ar',
  JO: 'ar', LB: 'ar', IQ: 'ar', MA: 'ar', DZ: 'ar', TN: 'ar', LY: 'ar',
  JP: 'ja',
};

const LOCALE_LOADERS = {
  es: () => import('@/translations/es.json'),
  ar: () => import('@/translations/ar.json'),
  ja: () => import('@/translations/ja.json'),
};

const localeCache = { en: EN_STRINGS };

async function loadLocaleStrings(lang) {
  if (localeCache[lang]) return localeCache[lang];
  const loader = LOCALE_LOADERS[lang];
  if (!loader) return EN_STRINGS;
  const mod = await loader();
  const strings = mod?.default ?? mod;
  localeCache[lang] = strings;
  return strings;
}

export const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState('en');
  const [strings, setStrings] = useState(EN_STRINGS);
  const [ready, setReady] = useState(true);
  const loadGen = useRef(0);

  const applyTheme = useCallback((lang) => {
    // One uniform visual theme for every language. Locale only sets text
    // direction (RTL) + the lang attribute — never the visual theme.
    [...document.body.classList].filter((c) => c.startsWith('theme-')).forEach((c) => document.body.classList.remove(c));
    document.body.classList.add('theme-en');
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
  }, []);

  const setLocale = useCallback((lang) => {
    if (!SUPPORTED.includes(lang)) lang = 'en';
    localStorage.setItem('lb_locale', lang);
    applyTheme(lang);
    setLocaleState(lang);

    if (lang === 'en') {
      setStrings(EN_STRINGS);
      setReady(true);
      return;
    }

    if (localeCache[lang]) {
      setStrings(localeCache[lang]);
      setReady(true);
      return;
    }

    const gen = ++loadGen.current;
    setReady(false);
    loadLocaleStrings(lang).then((next) => {
      if (loadGen.current !== gen) return;
      setStrings(next);
      setReady(true);
    }).catch(() => {
      if (loadGen.current !== gen) return;
      setStrings(EN_STRINGS);
      setReady(true);
    });
  }, [applyTheme]);

  useEffect(() => {
    async function init() {
      // 1. Explicit user choice wins — set via LanguageSwitcher or a prior
      //    auto-detection that we already persisted. Returns early so we
      //    never re-detect over their choice.
      const saved = localStorage.getItem('lb_locale');
      if (saved && SUPPORTED.includes(saved)) {
        setLocale(saved);
        return;
      }

      // 2. Render immediately in English so there's no flash of unstyled
      //    content while the geolocation API responds. We'll upgrade in
      //    place if detection picks a different locale.
      setLocale('en');

      // 3. Browser-language as a free, instant signal (no API call). If the
      //    user's OS / browser is set to a supported locale, use that.
      try {
        const nav = (navigator.language || navigator.userLanguage || 'en')
          .toLowerCase()
          .split('-')[0]; // "en-US" → "en", "es-MX" → "es"
        if (SUPPORTED.includes(nav) && nav !== 'en') {
          setLocale(nav);
          localStorage.setItem('lb_locale', nav);
          return; // browser-language match is enough — skip IP call
        }
      } catch { /* ignore */ }

      // 4. Skip the IP API if we've already tried it once this session — saves
      //    a request on every page navigation and respects the free 1000/day
      //    quota of ipapi.co.
      if (sessionStorage.getItem('lb_geo_tried') === '1') return;
      sessionStorage.setItem('lb_geo_tried', '1');

      // 5. IP-geolocation fallback. AbortController gives us a hard 1.5s
      //    timeout — if the API is slow or rate-limited, we stay on 'en'
      //    rather than hanging. Wrapped in try/catch so any failure
      //    (network, CORS, JSON parse, quota) silently degrades to 'en'.
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 1500);
        const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
        clearTimeout(timer);
        if (!res.ok) return;
        const data = await res.json();
        const detected = COUNTRY_LANG[data?.country_code];
        if (detected && SUPPORTED.includes(detected) && detected !== 'en') {
          setLocale(detected);
          localStorage.setItem('lb_locale', detected);
        }
      } catch {
        /* timeout / network / CORS — already on 'en', user is fine */
      }
    }
    init();
  }, [setLocale]);

  const t = useCallback((key) => strings[key] || key, [strings]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t, ready }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LocaleContext);
  if (!ctx) return { locale: 'en', setLocale: () => {}, t: (k) => k, ready: true };
  return ctx;
}
